// Minimax provider — adapts MiniMax's OpenAI-compatible chat completions
// endpoint to LLMProvider.
//
// Endpoint:  POST https://api.minimax.io/v1/text/chatcompletion_v2
// Auth:      Bearer <api_key>
// Streaming: SSE; each frame is `data: { ... }` JSON, terminated by `data: [DONE]`.
// Format:    OpenAI-compatible messages[] + tools[] + tool_calls[] shape.
//
// Differences from Gemini:
//   - 'system' is just another message in messages[] (no separate field).
//   - Assistant tool calls live in `message.tool_calls[]`, with the
//     function name + a STRINGIFIED JSON of arguments.
//   - Tool results are messages with role: 'tool' + tool_call_id +
//     stringified content.
//   - Streaming deltas can split tool_call.function.arguments across
//     multiple chunks; we accumulate per (tool_call index) → emit
//     tool_use_complete only when the whole call has arrived.
//
// Caveat (flagged in the workflow chat): minimax's tool-use reliability
// in long agentic loops is less battle-tested than Anthropic / Gemini.
// If a turn produces malformed JSON in arguments, we surface the parse
// error so the loop can ask the user to switch to Gemini.

import type {
  LLMProvider,
  Message,
  ProviderStreamEvent,
  ToolDefinition,
  UsageReport,
} from '../agent/types';

interface MinimaxToolCallDelta {
  index: number;
  id?: string;
  type?: 'function';
  function?: {
    name?: string;
    arguments?: string;
  };
}

interface MinimaxStreamDelta {
  role?: string;
  content?: string;
  tool_calls?: MinimaxToolCallDelta[];
}

interface MinimaxStreamChoice {
  index: number;
  delta?: MinimaxStreamDelta;
  finish_reason?: string;
}

interface MinimaxUsage {
  prompt_tokens?: number;
  completion_tokens?: number;
  total_tokens?: number;
}

interface MinimaxStreamChunk {
  id?: string;
  object?: string;
  choices?: MinimaxStreamChoice[];
  usage?: MinimaxUsage;
}

export interface MinimaxProviderOptions {
  apiKey: string;
  /** Defaults to 'MiniMax-M1'. Use 'abab6.5s-chat' for cheaper / faster. */
  model?: string;
  /**
   * Endpoint base. Defaults to the international (.io) host. Mainland
   * users may need to override with the .chat host.
   */
  apiBase?: string;
}

export class MinimaxProvider implements LLMProvider {
  readonly slug = 'minimax' as const;
  readonly displayName: string;
  readonly model: string;
  private readonly apiKey: string;
  private readonly apiBase: string;

  constructor(opts: MinimaxProviderOptions) {
    if (!opts.apiKey?.trim()) {
      throw new Error('MinimaxProvider: apiKey is required');
    }
    this.apiKey = opts.apiKey.trim();
    this.model = opts.model ?? 'MiniMax-M1';
    this.apiBase = (opts.apiBase ?? 'https://api.minimax.io').replace(/\/$/, '');
    this.displayName = `MiniMax ${this.model}`;
  }

  async streamChat(args: {
    messages: Message[];
    tools: ToolDefinition[];
    signal?: AbortSignal;
    onEvent: (e: ProviderStreamEvent) => void;
  }): Promise<void> {
    const { messages, tools, signal, onEvent } = args;

    const body: Record<string, unknown> = {
      model: this.model,
      messages: toMinimaxMessages(messages),
      stream: true,
    };
    if (tools.length > 0) {
      body.tools = tools.map(toMinimaxTool);
      body.tool_choice = 'auto';
    }

    const url = `${this.apiBase}/v1/text/chatcompletion_v2`;
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${this.apiKey}`,
      },
      body: JSON.stringify(body),
      signal,
    });

    if (!res.ok || !res.body) {
      const txt = await res.text().catch(() => '');
      throw new Error(`Minimax ${res.status}: ${txt.slice(0, 500)}`);
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    // OpenAI-style streaming splits one tool_call's `arguments` JSON
    // across many delta chunks. We accumulate by tool-call index and
    // emit tool_use_start the first time we see an index, tool_use_input_delta
    // for each fragment, and tool_use_complete when the assistant turn ends.
    const toolBuffers = new Map<
      number,
      { id: string; name: string; argsJson: string }
    >();
    const finishedUsage: UsageReport = { inputTokens: 0, outputTokens: 0 };
    let messageEnded = false;

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx: number;
        while ((idx = buffer.indexOf('\n\n')) !== -1) {
          const frame = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const dataLine = frame
            .split('\n')
            .find((l) => l.startsWith('data:'))
            ?.slice(5)
            .trim();
          if (!dataLine) continue;
          if (dataLine === '[DONE]') {
            messageEnded = true;
            continue;
          }
          let chunk: MinimaxStreamChunk;
          try {
            chunk = JSON.parse(dataLine);
          } catch {
            continue;
          }
          const choice = chunk.choices?.[0];
          const delta = choice?.delta;

          if (delta?.content) {
            onEvent({ type: 'text_delta', text: delta.content });
          }

          if (delta?.tool_calls) {
            for (const tc of delta.tool_calls) {
              let buf = toolBuffers.get(tc.index);
              if (!buf) {
                // First sight of this tool call — synthesize start event.
                const id = tc.id ?? `tc_${tc.index}`;
                const name = tc.function?.name ?? '';
                buf = { id, name, argsJson: '' };
                toolBuffers.set(tc.index, buf);
                if (name) {
                  onEvent({ type: 'tool_use_start', id, name });
                }
              } else if (tc.function?.name && !buf.name) {
                // Some streams send name in a later chunk — back-fill + emit start.
                buf.name = tc.function.name;
                onEvent({ type: 'tool_use_start', id: buf.id, name: buf.name });
              }
              if (tc.function?.arguments) {
                buf.argsJson += tc.function.arguments;
                onEvent({
                  type: 'tool_use_input_delta',
                  id: buf.id,
                  partialJson: tc.function.arguments,
                });
              }
            }
          }

          if (chunk.usage) {
            finishedUsage.inputTokens = chunk.usage.prompt_tokens ?? 0;
            finishedUsage.outputTokens = chunk.usage.completion_tokens ?? 0;
          }

          if (choice?.finish_reason) {
            messageEnded = true;
          }
        }
      }

      // End of stream — finalize accumulated tool calls.
      for (const [, buf] of toolBuffers) {
        let parsed: Record<string, unknown> = {};
        const trimmed = buf.argsJson.trim();
        if (trimmed.length > 0) {
          try {
            parsed = JSON.parse(trimmed) as Record<string, unknown>;
          } catch (err) {
            throw new Error(
              `Minimax: tool '${buf.name}' returned malformed JSON args (${
                err instanceof Error ? err.message : String(err)
              }). Raw: ${trimmed.slice(0, 200)}`,
            );
          }
        }
        onEvent({
          type: 'tool_use_complete',
          id: buf.id,
          name: buf.name,
          input: parsed,
        });
      }

      // Always emit message_complete so the loop sees a usage tally even
      // if the provider didn't include one (some plans omit it on certain
      // streaming paths).
      void messageEnded;
      onEvent({ type: 'message_complete', usage: finishedUsage });
    } catch (err) {
      if ((err as { name?: string })?.name === 'AbortError') throw err;
      throw err;
    } finally {
      try {
        reader.releaseLock();
      } catch {
        /* ignore */
      }
    }
  }
}

// ---- Translation helpers -------------------------------------------------

interface MinimaxOutMessage {
  role: 'system' | 'user' | 'assistant' | 'tool';
  content?: string;
  tool_call_id?: string;
  tool_calls?: Array<{
    id: string;
    type: 'function';
    function: { name: string; arguments: string };
  }>;
}

function toMinimaxMessages(messages: Message[]): MinimaxOutMessage[] {
  const out: MinimaxOutMessage[] = [];
  for (const msg of messages) {
    if (msg.role === 'system') {
      out.push({ role: 'system', content: msg.content });
      continue;
    }
    if (msg.role === 'user') {
      out.push({ role: 'user', content: msg.content });
      continue;
    }
    if (msg.role === 'assistant') {
      const text: string[] = [];
      const toolCalls: NonNullable<MinimaxOutMessage['tool_calls']> = [];
      for (const block of msg.content) {
        if (block.type === 'text' && block.text) {
          text.push(block.text);
        } else if (block.type === 'tool_use') {
          toolCalls.push({
            id: block.id,
            type: 'function',
            function: {
              name: block.name,
              arguments: JSON.stringify(block.input ?? {}),
            },
          });
        }
      }
      const m: MinimaxOutMessage = { role: 'assistant' };
      if (text.length) m.content = text.join('');
      if (toolCalls.length) m.tool_calls = toolCalls;
      out.push(m);
      continue;
    }
    if (msg.role === 'tool_result') {
      out.push({
        role: 'tool',
        tool_call_id: msg.toolUseId,
        content: msg.isError ? `[error] ${msg.content}` : msg.content,
      });
      continue;
    }
  }
  return out;
}

function toMinimaxTool(tool: ToolDefinition): {
  type: 'function';
  function: {
    name: string;
    description: string;
    parameters: ToolDefinition['inputSchema'];
  };
} {
  return {
    type: 'function',
    function: {
      name: tool.name,
      description: tool.description,
      parameters: tool.inputSchema,
    },
  };
}
